import * as React from 'react';
import axios from 'axios';
import { LearnSite } from '@bb-learn-instances-service/common';
import { getCurrentLearnInstance } from 'components/TenantList';
import { InstanceInfo } from 'components/TenantList/TenantListTable';
import { useAuthContext } from 'contexts/AuthContext';
import { apiUrl } from 'utils';

// Fetches instance info for every given tenant up front (in parallel, via one effect) instead of
// one request per visible row - so fields like installationId/url are known for the whole list
// as soon as it loads, and can be searched/sorted against. A tenant missing from the returned map
// means its request is still in flight. A tenant whose request failed is in the map with an
// undefined value (a single 404 shouldn't blank out the others, hence allSettled).
export const useInstanceInfoMap = (tenantIds: string[]) => {
  const { idToken } = useAuthContext();
  const [data, setData] = React.useState<Record<string, InstanceInfo | undefined>>({});
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let stale = false;

    if (tenantIds.length === 0) {
      setData({});
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const headers = { Authorization: `Bearer ${idToken}` };
    Promise.allSettled(
      tenantIds.map((tenantId) =>
        axios
          .get<LearnSite>(apiUrl('lct', `tenants/${tenantId}/instances/learnSites`), { headers })
          .then(({ data }) => [tenantId, getCurrentLearnInstance(data?.items)] as const),
      ),
    ).then((results) => {
      if (stale) return;
      const map: Record<string, InstanceInfo | undefined> = {};
      results.forEach((result, i) => {
        map[tenantIds[i]] = result.status === 'fulfilled' ? result.value[1] : undefined;
      });
      setData(map);
      setLoading(false);
    });

    return () => {
      stale = true;
    };
  }, [tenantIds, idToken]);

  return { data, loading };
};

export default useInstanceInfoMap;
